import React, { useEffect, useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { Notification } from '../SocialVideoSlide.styles';

const YoutubePlayer = ({ videoId, title, isClicked, onVideoEnd }) => {
    const playerRef = useRef(null);
    const [player, setPlayer] = useState(null);
    const [youtubeReady, setYoutubeReady] = useState(false);
    const [showNotification, setShowNotification] = useState(false);

    const initializePlayer = useCallback(() => {
        if (!videoId || typeof window === 'undefined' || !youtubeReady) return;
        window.YT.ready(() => {
            const player = new window.YT.Player(playerRef.current, {
                videoId: videoId,
                events: {
                    onReady: (event) => {
                        console.log('YoutubePlayer: Player is ready');
                        setPlayer(event.target);
                        if (isClicked) {
                            event.target.playVideo();
                        }
                    },
                    onStateChange: (event) => {
                        if (event.data === window.YT.PlayerState.ENDED) {
                            console.log('YoutubePlayer: Video has ended');
                            if (onVideoEnd) {
                                onVideoEnd();
                            }
                        }
                    },
                },
            });
        });
    }, [videoId, youtubeReady, isClicked, onVideoEnd]);

    useEffect(() => {
        const handleYouTubeIframeAPIReady = () => {
            setYoutubeReady(true);
        };

        document.addEventListener('youtubeIframeAPIReady', handleYouTubeIframeAPIReady);

        return () => {
            document.removeEventListener('youtubeIframeAPIReady', handleYouTubeIframeAPIReady);
            if (player) {
                player.destroy();
            }
        };
    }, [player]);

    useEffect(() => {
        if (youtubeReady) {
            initializePlayer();
        }
    }, [youtubeReady, initializePlayer]);

    useEffect(() => {
        if (player && isClicked) {
            player.playVideo();
        }
    }, [player, isClicked]);

    const togglePlayback = () => {
        if (player) {
            if (player.getPlayerState() === window.YT.PlayerState.PLAYING) {
                player.pauseVideo();
            } else {
                player.playVideo();
            }
        }
    };

    const handlePlayClick = () => {
        if (!youtubeReady) {
            setShowNotification(true);
        } else if (player) {
            player.playVideo();
        }
    };

    return (
        <>
            <div ref={playerRef} aria-label="YouTube video player" title={title} onClick={handlePlayClick}></div>
            {showNotification && (
                <Notification>
                    <p>To view this video, please accept the cookie policy to enable YouTube content.</p>
                    <button onClick={() => setShowNotification(false)}>Close</button>
                </Notification>
            )}
        </>
    );
};

YoutubePlayer.propTypes = {
    videoId: PropTypes.string.isRequired,
    title: PropTypes.string,
    isClicked: PropTypes.bool,
    onVideoEnd: PropTypes.func,
};

export default YoutubePlayer;